import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface SearchBarProps {
  isSearchExpanded: boolean;
  setIsSearchExpanded: (value: boolean) => void;
  isDesktop?: boolean;
}

const NavigationSearchBar: React.FC<SearchBarProps> = ({ 
  isSearchExpanded, 
  setIsSearchExpanded,
  isDesktop = true
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchExpanded]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchExpanded(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsSearchExpanded(false);
    }
  };

  return (
    <form onSubmit={handleSearch} className="relative">
      <Button 
        type="button"
        variant="ghost" 
        size="icon" 
        onClick={() => setIsSearchExpanded(!isSearchExpanded)}
        className={cn(
          "absolute right-0 transition-opacity duration-200",
          isSearchExpanded ? "opacity-0 pointer-events-none" : "opacity-100"
        )}
      >
        <Search size={20} className="text-brand-primary" />
      </Button>

      <div 
        className={cn(
          "relative transition-all duration-200 ease-out",
          isDesktop ? "w-[320px]" : "w-full",
          isSearchExpanded 
            ? "opacity-100 translate-y-0" 
            : "opacity-0 -translate-y-2 pointer-events-none"
        )}
      >
        <div className="relative bg-white rounded-lg shadow-sm">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Search size={18} />
          </div>

          <Input
            ref={searchInputRef}
            type="text"
            placeholder={isDesktop ? "Rechercher une activité..." : "Rechercher..."}
            className="h-10 pl-10 pr-10 border-none bg-transparent focus-visible:ring-1 focus-visible:ring-brand-primary/20"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />

          <Button 
            type="button"
            variant="ghost" 
            size="icon"
            onClick={() => setIsSearchExpanded(false)}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 hover:bg-gray-100"
          >
            <X size={18} className="text-gray-400" />
          </Button>
        </div>
      </div>
    </form>
  );
};

export default NavigationSearchBar;